import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, Video, X, ArrowLeft, ArrowRight, Check, AlertTriangle, RotateCcw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Chrome-less, brand-themed booking manager (reschedule / cancel) for iframe embeds.
 * URL: /embed/manage?token=<manage_token>&brand=<slug>
 */
export default function EmbedManagePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const brandSlug = params.get('brand') || 'dca';

  const [brand, setBrand] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('view'); // view | reschedule | cancel | done
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [doneMessage, setDoneMessage] = useState('');

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    document.body.style.background = 'transparent';
    axios.get(`${API_URL}/api/booking-brands/${brandSlug}`).then((r) => setBrand(r.data)).catch(() => {});
  }, [brandSlug]);

  useEffect(() => {
    if (!token) { setError('No booking link provided'); setLoading(false); return; }
    axios.get(`${API_URL}/api/bookings/manage/${token}`)
      .then((res) => setBooking(res.data))
      .catch((e) => setError(e.response?.data?.detail || 'Could not load booking'))
      .finally(() => setLoading(false));
  }, [token]);

  const theme = {
    primary: brand?.primary_color || '#1D4ED8',
    accent: brand?.accent_color || '#F97316',
  };

  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${hour}:${String(m).padStart(2, '0')} ${period}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return { firstDay: new Date(year, month, 1).getDay(), daysInMonth: new Date(year, month + 1, 0).getDate(), year, month };
  };

  const handleDateClick = async (day) => {
    const { year, month } = getDaysInMonth(calendarMonth);
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${API_URL}/api/booking-slots/${dateStr}`);
      setSlots(res.data.slots || []);
    } catch { setSlots([]); } finally { setLoadingSlots(false); }
  };

  const handleCancel = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/bookings/manage/${token}/cancel`);
      setDoneMessage('Your session has been cancelled. A confirmation email is on its way.');
      setMode('done');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel session');
    } finally { setSubmitting(false); }
  };

  const handleReschedule = async () => {
    if (!selectedDate || !selectedSlot) { toast.error('Pick a new date and time first'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/bookings/manage/${token}/reschedule`, { date: selectedDate, time: selectedSlot });
      setBooking({ ...booking, date: res.data.date, time: res.data.time, zoom_join_url: res.data.zoom_join_url, zoom_password: res.data.zoom_password });
      setDoneMessage(`Your session has been moved to ${res.data.date} at ${formatTime(res.data.time)}. An updated email is on its way.`);
      setMode('done');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reschedule');
    } finally { setSubmitting(false); }
  };

  const { firstDay, daysInMonth, year, month } = getDaysInMonth(calendarMonth);

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center bg-transparent">
        <div className="w-10 h-10 border-4 border-[#94A3B8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto bg-white p-8 border border-[#E2E8F0] rounded-2xl shadow-sm text-center my-8" data-testid="embed-manage-error">
        <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={26} className="text-red-600" />
        </div>
        <h2 className="text-xl font-bold text-[#0A0F1C] mb-2">Link not valid</h2>
        <p className="text-[#475569] text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-[400px] bg-transparent p-4" data-testid="embed-manage-page">
      <div className="max-w-xl mx-auto bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-6 sm:p-7">
        {brand?.logo_url ? (
          <img src={brand.logo_url} alt={brand.name} className="h-12 mx-auto mb-2 object-contain" data-testid="embed-manage-logo" />
        ) : (
          <div className="text-center font-bold text-lg mb-2" style={{ color: theme.primary }}>{brand?.name}</div>
        )}
        <h1 className="text-xl font-bold text-[#0A0F1C] text-center mb-1">Manage your session</h1>
        <p className="text-[#94A3B8] text-sm text-center mb-5">Reschedule or cancel anytime.</p>

        {/* Current booking summary */}
        <div className="border rounded-xl p-4 mb-5" style={{ background: theme.primary + '0D', borderColor: theme.primary + '33' }}>
          <div className="text-[10px] font-bold tracking-wider uppercase mb-2" style={{ color: theme.primary }}>Current Session</div>
          <div className="flex items-center gap-2 mb-1.5"><Calendar size={14} style={{ color: theme.primary }} /><span className="text-sm font-semibold text-[#0A0F1C]" data-testid="embed-current-date">{new Date(booking.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span></div>
          <div className="flex items-center gap-2 mb-1.5"><Clock size={14} style={{ color: theme.primary }} /><span className="text-sm font-semibold text-[#0A0F1C]" data-testid="embed-current-time">{formatTime(booking.time)}</span></div>
          {booking.zoom_join_url && (
            <div className="flex items-start gap-2 mt-2"><Video size={14} style={{ color: theme.primary, marginTop: 2 }} /><a href={booking.zoom_join_url} target="_blank" rel="noopener noreferrer" className="text-xs hover:underline break-all" style={{ color: theme.primary }}>{booking.zoom_join_url}</a></div>
          )}
          <div className="text-xs text-[#94A3B8] mt-2">Booked under: {booking.name} &middot; {booking.email}</div>
        </div>

        {booking.status === 'cancelled' && mode !== 'done' && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800 text-center" data-testid="embed-already-cancelled">This booking has been cancelled.</div>
        )}

        {/* View mode */}
        {mode === 'view' && booking.status !== 'cancelled' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="embed-manage-actions">
            <button onClick={() => setMode('reschedule')} className="w-full text-white font-bold text-sm px-4 py-2.5 rounded-lg inline-flex items-center justify-center gap-2" style={{ background: theme.primary }} data-testid="embed-open-reschedule-btn">
              <RotateCcw size={14} /> Reschedule
            </button>
            <button onClick={() => setMode('cancel')} className="w-full bg-white border border-red-200 text-red-600 hover:bg-red-50 font-semibold text-sm px-4 py-2.5 rounded-lg inline-flex items-center justify-center gap-2" data-testid="embed-open-cancel-btn">
              <X size={14} /> Cancel Session
            </button>
          </div>
        )}

        {/* Cancel confirmation */}
        {mode === 'cancel' && (
          <div data-testid="embed-cancel-step">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-bold text-red-900 text-sm">Are you sure?</div>
                  <p className="text-sm text-red-800 mt-1">This will release your time slot and cancel your Zoom meeting. You can always rebook later.</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button onClick={() => setMode('view')} disabled={submitting} className="flex-1 border border-[#E2E8F0] rounded-lg text-sm font-semibold text-[#475569] hover:bg-[#F8FAFC] px-4 py-2.5 inline-flex items-center justify-center gap-2"><ArrowLeft size={14} /> Keep my session</button>
              <button onClick={handleCancel} disabled={submitting} className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-lg px-4 py-2.5" data-testid="embed-confirm-cancel-btn">
                {submitting ? 'Cancelling...' : 'Yes, cancel'}
              </button>
            </div>
          </div>
        )}

        {/* Reschedule flow */}
        {mode === 'reschedule' && (
          <div data-testid="embed-reschedule-step">
            <button onClick={() => { setMode('view'); setSelectedDate(null); setSelectedSlot(null); }} className="text-sm mb-4 flex items-center gap-1 font-medium hover:underline" style={{ color: theme.primary }} data-testid="embed-reschedule-back-btn"><ArrowLeft size={14} /> Back</button>
            <h3 className="font-bold text-[#0A0F1C] text-base mb-3">Pick a new date</h3>
            <div className="border border-[#E2E8F0] rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))} className="p-1.5 hover:bg-[#F1F5F9] rounded"><ArrowLeft size={16} /></button>
                <h4 className="font-bold text-[#0A0F1C] text-sm">{calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h4>
                <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))} className="p-1.5 hover:bg-[#F1F5F9] rounded"><ArrowRight size={16} /></button>
              </div>
              <div className="grid grid-cols-7 gap-1 mb-1">
                {dayNames.map(d => <div key={d} className="text-center text-[10px] font-medium text-[#94A3B8] py-1">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: firstDay }, (_, i) => <div key={`e-${i}`}></div>)}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const dateObj = new Date(year, month, day);
                  const isPast = dateObj < today;
                  const dow = dateObj.getDay();
                  const isSunday = dow === 0;
                  const isSecondSaturday = dow === 6 && day >= 8 && day <= 14;
                  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const isSelected = selectedDate === dateStr;
                  const isDisabled = isPast || isSunday || isSecondSaturday;
                  return (
                    <button
                      key={day}
                      onClick={() => !isDisabled && handleDateClick(day)}
                      disabled={isDisabled}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-all mx-auto flex items-center justify-center ${isDisabled ? 'text-[#D1D5DB] cursor-not-allowed' : 'hover:bg-[#F1F5F9] text-[#0A0F1C]'}`}
                      style={isSelected ? { background: theme.primary, color: '#fff' } : {}}
                      data-testid={`embed-r-cal-day-${day}`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedDate && (
              <div className="mb-4">
                <h4 className="font-bold text-[#0A0F1C] text-sm mb-2">Available times</h4>
                {loadingSlots ? (
                  <div className="py-4 text-center"><div className="w-6 h-6 border-2 border-[#94A3B8] border-t-transparent rounded-full animate-spin mx-auto"></div></div>
                ) : slots.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {slots.map((slot, i) => (
                      <button
                        key={slot.start}
                        onClick={() => setSelectedSlot(slot.start)}
                        className="py-2 px-3 text-xs rounded-lg border transition-all font-medium"
                        style={selectedSlot === slot.start ? { background: theme.primary, color: '#fff', borderColor: theme.primary } : { borderColor: '#E2E8F0', color: '#475569' }}
                        data-testid={`embed-r-time-${i}`}
                      >
                        {formatTime(slot.start)}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-[#475569] text-center py-3">No slots available on this date</p>
                )}
              </div>
            )}

            {selectedDate && selectedSlot && (
              <button onClick={handleReschedule} disabled={submitting} className="w-full text-white font-bold text-sm px-4 py-2.5 rounded-lg" style={{ background: theme.primary }} data-testid="embed-confirm-reschedule-btn">
                {submitting ? 'Updating...' : `Confirm new time: ${selectedDate} at ${formatTime(selectedSlot)}`}
              </button>
            )}
          </div>
        )}

        {/* Done */}
        {mode === 'done' && (
          <div className="text-center py-2" data-testid="embed-manage-done">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: theme.primary + '20' }}>
              <Check size={24} style={{ color: theme.primary }} />
            </div>
            <h2 className="text-xl font-bold text-[#0A0F1C] mb-2">All set</h2>
            <p className="text-[#475569] text-sm">{doneMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
}
